import React, { useMemo, useState } from "react";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError } from "~/lib/utils";
import { useCreateConversationMutation } from "~/nav/panels/AskFellow/AskFellowHooks";
import toast from "~/ui/toast";

export const MeetingModalContext = React.createContext<{
	isAskFellowOpen: boolean;
	setIsAskFellowOpen: (isOpen: boolean) => void;
	onOpenAskFellow: () => void;
} | null>(null);

export function MeetingModalContextProvider({
	children,
	noteId,
}: {
	children: React.ReactNode;
	noteId?: string | null;
}) {
	const [isAskFellowOpen, setIsAskFellowOpen] = useState(false);

	const [createConversation, mutationInProgress] = useCreateConversationMutation();
	const onOpenAskFellow = React.useCallback(async () => {
		if (mutationInProgress || !noteId) return;

		try {
			await createConversation(noteId);
			setIsAskFellowOpen(true);
			dispatchAnalyticsTrack("[Ask Copilot] Modal opened", { source: "floating_note_button" });
		} catch (e) {
			const message = getFirstUserFacingError(e) ?? "Something went wrong; please try again later.";
			toast.error(message);
		}
	}, [noteId, createConversation, mutationInProgress]);

	const value = useMemo(
		() => ({ isAskFellowOpen, setIsAskFellowOpen, onOpenAskFellow }),
		[isAskFellowOpen, setIsAskFellowOpen, onOpenAskFellow],
	);

	return <MeetingModalContext.Provider value={value}>{children}</MeetingModalContext.Provider>;
}

export function useMeetingModalContext() {
	return React.useContext(MeetingModalContext);
}
